/** The lossy half of the transport: cues, deduped on envelope id and shed under backpressure. */

import type { CueFrame } from "@nebulize/sdk";

import { CUE_DEDUPE_WINDOW, createCueMemory } from "./cueMemory";
import { hasSnapshot, useSnapshotStore } from "./snapshotStore";

/** Cues handed to the renderer in one drain. Past it a burst sheds rather than queues. */
export const CUE_QUEUE_BOUND = 32;

export { CUE_DEDUPE_WINDOW };

/** What became of one offered cue. Every verdict but `queued` is counted, never silent. */
export type CueVerdict = "queued" | "deduped" | "shed" | "discarded";

export interface CueCounts {
  /** Cues offered by the transport, whatever became of them. */
  readonly received: number;
  /** Cues handed to the renderer. */
  readonly delivered: number;
  /** Repeats of an envelope id already seen in the window. */
  readonly deduped: number;
  /** Cues dropped because the queue was full. */
  readonly shed: number;
  /** Cues dropped because the page holds no snapshot and may not paint. */
  readonly discarded: number;
}

export type CueListener = (frame: CueFrame) => void;

/** What a renderer needs of the channel: the cues that survived, and nothing else. */
export interface CueStream {
  subscribe: (listener: CueListener) => () => void;
}

export interface CueChannel extends CueStream {
  /** Take one cue off the transport. Returns what became of it. */
  offer: (frame: CueFrame) => CueVerdict;
  counts: () => CueCounts;
  /** Drop everything in flight. The counters are cumulative and survive. */
  reset: () => void;
}

export interface CueChannelOptions {
  /** The paint gate. A cue arriving before the first snapshot is discarded, never queued. */
  readonly ready?: () => boolean;
  /** How many cues may wait for the next drain. */
  readonly bound?: number;
  /** How many envelope ids the dedupe remembers. */
  readonly dedupeWindow?: number;
  /** When the queue is handed over. One drain per rendered frame keeps a burst off the frame. */
  readonly schedule?: (drain: () => void) => void;
}

function paintable(): boolean {
  return hasSnapshot(useSnapshotStore.getState());
}

function onNextFrame(drain: () => void): void {
  globalThis.requestAnimationFrame(() => {
    drain();
  });
}

export function createCueChannel(options: CueChannelOptions = {}): CueChannel {
  const ready = options.ready ?? paintable;
  const bound = options.bound ?? CUE_QUEUE_BOUND;
  const remembered = options.dedupeWindow ?? CUE_DEDUPE_WINDOW;
  const schedule = options.schedule ?? onNextFrame;

  const listeners = new Set<CueListener>();
  const seen = createCueMemory(remembered);
  const tally = { received: 0, delivered: 0, deduped: 0, shed: 0, discarded: 0 };
  let queue: CueFrame[] = [];
  let scheduled = false;

  function drain(): void {
    scheduled = false;
    const handing = queue;
    queue = [];
    tally.delivered += handing.length;
    for (const frame of handing) {
      for (const listener of listeners) listener(frame);
    }
  }

  return {
    offer: (frame) => {
      tally.received += 1;

      if (!ready()) {
        tally.discarded += 1;
        return "discarded";
      }
      if (seen.saw(frame.envelope_id)) {
        tally.deduped += 1;
        return "deduped";
      }
      if (queue.length >= bound) {
        tally.shed += 1;
        return "shed";
      }

      seen.remember(frame.envelope_id);
      queue.push(frame);
      if (!scheduled) {
        scheduled = true;
        schedule(drain);
      }
      return "queued";
    },

    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },

    counts: () => ({ ...tally }),

    reset: () => {
      queue = [];
      seen.clear();
      scheduled = false;
    },
  };
}

/** The one channel the host connection feeds and the scene reads. */
export const cueChannel: CueChannel = createCueChannel();
